/**
 * Cross-BU handoff primitives. Extracted from `App.tsx` per ticket
 * superall-shell-01.
 *
 * `BU_SURFACES` is the static directory of consumer-facing BUs the
 * super-app can hand off to. `handoffToBU` exchanges the Superall JWT
 * for a BU-native session token and redirects the browser to the BU's
 * origin — the user lands already signed in.
 *
 * Backend contract: `POST {API_BASE}{handoffApiPath}` accepts the
 * Superall Bearer token and returns `{ token, refreshToken? }`. The
 * routes live under `/api/{mall,residence,cinema,fnb}/...` per the
 * BU-specific handoff routers in `artifacts/api-server/`.
 */

import { API_BASE } from "./api";

const OMALL_ORIGIN =
  import.meta.env.VITE_OMALL_URL ?? "http://localhost:21973";
const RESIDENCE_ORIGIN =
  import.meta.env.VITE_RESIDENCE_URL ?? "http://localhost:22490";
const CINEMA_ORIGIN =
  import.meta.env.VITE_CINEMA_URL ?? "http://localhost:5181";
const FNB_ORIGIN = import.meta.env.VITE_FNB_URL ?? "http://localhost:5182";

export type BUId =
  | "omall"
  | "park"
  | "loyalty"
  | "cinema"
  | "fnb"
  | "partner";

const LOYALTY_ORIGIN =
  import.meta.env.VITE_LOYALTY_URL ?? "http://localhost:5184";

export type BUStatus = "live" | "beta" | "soon";

export interface BUSurface {
  id: BUId;
  nameAr: string;
  nameEn: string;
  descAr: string;
  descEn: string;
  /** Production redirect origin for the BU surface */
  href: string;
  /** Dev redirect origin for the BU surface */
  devHref: string;
  /** localStorage key for the BU's own session token */
  buTokenKey: string;
  /**
   * API-server path to the handoff endpoint, prefixed with API_BASE.
   * e.g. "/mall/customers/auth/superall-handoff". `null` for "soon"
   * BUs that don't have a handoff endpoint yet.
   */
  handoffApiPath: string | null;
  status: BUStatus;
  icon: string;
}

export const BU_SURFACES: BUSurface[] = [
  {
    id: "omall",
    nameAr: "أو مول",
    nameEn: "O Mall",
    descAr: "تسوق الآن",
    descEn: "Shop Now",
    href: `${OMALL_ORIGIN}`,
    devHref: OMALL_ORIGIN,
    buTokenKey: "mall_token",
    handoffApiPath: "/mall/customers/auth/superall-handoff",
    status: "live",
    icon: "🏬",
  },
  {
    id: "park",
    nameAr: "العثيم بارك",
    nameEn: "Residential Park",
    descAr: "مشاريعي",
    descEn: "My Projects",
    href: `${RESIDENCE_ORIGIN}`,
    devHref: RESIDENCE_ORIGIN,
    buTokenKey: "park_token",
    handoffApiPath: "/residence/auth/superall-handoff",
    status: "live",
    icon: "🏠",
  },
  {
    id: "cinema",
    nameAr: "السينما",
    nameEn: "Cinema",
    descAr: "احجز تذكرة",
    descEn: "Book Tickets",
    href: `${CINEMA_ORIGIN}`,
    devHref: CINEMA_ORIGIN,
    buTokenKey: "cinema_token",
    handoffApiPath: "/v1/cinema/auth/superall-handoff",
    status: "live",
    icon: "🎬",
  },
  {
    id: "fnb",
    nameAr: "الطعام والشراب",
    nameEn: "Food & Beverage",
    descAr: "اطلب الآن",
    descEn: "Order Now",
    href: `${FNB_ORIGIN}`,
    devHref: FNB_ORIGIN,
    buTokenKey: "fnb_token",
    handoffApiPath: "/v1/fnb/auth/superall-handoff",
    status: "live",
    icon: "🍽️",
  },
  {
    id: "loyalty",
    nameAr: "الولاء",
    nameEn: "Loyalty",
    descAr: "نقاطي",
    descEn: "My Points",
    href: `${LOYALTY_ORIGIN}`,
    devHref: LOYALTY_ORIGIN,
    buTokenKey: "loyalty_token",
    handoffApiPath: "/v1/loyalty/handoff",
    status: "live",
    icon: "⭐",
  },
];

/**
 * Exchange the Superall JWT for a BU-native session token. The handoff
 * API path may be absolute (`/api/...`) or platform-relative
 * (`/mall/...`); both are joined to API_BASE the same way as in the
 * original monolith.
 */
export async function handoffToBU(
  bu: BUSurface,
  superallToken: string,
  isDev: boolean,
): Promise<void> {
  const redirectOrigin = isDev ? bu.devHref : bu.href;

  if (!bu.handoffApiPath) {
    // Should never reach here — tile is disabled — but guard anyway.
    window.location.href = redirectOrigin;
    return;
  }

  // API_BASE ends with "/api"; all handoffApiPath values are relative to that
  // (e.g. "/mall/...", "/v1/cinema/..."). Simple concatenation is correct.
  const handoffUrl = `${API_BASE}${bu.handoffApiPath}`;

  const res = await fetch(handoffUrl, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${superallToken}`,
    },
    body: JSON.stringify({ bu: bu.id }),
  });

  if (res.ok) {
    const data = (await res.json()) as {
      token: string;
      refreshToken?: string;
    };
    if (data.token) {
      localStorage.setItem(bu.buTokenKey, data.token);
    }
    if (data.refreshToken) {
      localStorage.setItem(`${bu.buTokenKey}_refresh`, data.refreshToken);
    }
  } else if (res.status === 404) {
    // NO_MALL_ACCOUNT or similar — redirect to BU login; the user
    // registers there. Preserves the runtime parity from the monolith.
    window.location.href = redirectOrigin;
    return;
  } else {
    const body = (await res.json().catch(() => ({}))) as {
      error?: string;
      message?: string;
    };
    throw new Error(body.message ?? `Handoff failed (${res.status})`);
  }

  window.location.href = redirectOrigin;
}

/**
 * Extract and store a BU token delivered via the URL hash fragment.
 *
 * SUPER-P3-012 / SUPER-P1-005 hash-fragment delivery fix:
 * When `handoffToBU()` redirects to the BU origin it appends the BU token in
 * the URL hash as `#bu_token=<jwt>` (cross-origin safe — hash is never sent
 * to the server). The BU app calls `receiveHandoffToken(bu)` on mount to
 * extract the token and persist it to localStorage.
 *
 * Priority order:
 *   1. `#bu_token=<jwt>` query parameter inside the fragment (primary path)
 *   2. `window[__bu_token_<id>__]` transient global (legacy fallback if the BU
 *      app sets a global before calling this function)
 *
 * Returns the extracted token string, or `null` if nothing was found.
 * Side-effect: writes the token to `localStorage[bu.buTokenKey]` and clears
 * the hash from the URL so the token is not visible in the browser history.
 */
export function receiveHandoffToken(bu: BUSurface): string | null {
  // 1. Parse the URL hash fragment
  const hash = typeof window !== "undefined" ? window.location.hash : "";
  if (hash) {
    // Strip leading "#" and parse as URLSearchParams
    const params = new URLSearchParams(hash.slice(1));
    const hashToken = params.get("bu_token");
    if (hashToken) {
      localStorage.setItem(bu.buTokenKey, hashToken);
      // Remove the hash from the URL so the token is not in the history entry
      try {
        const clean = window.location.pathname + window.location.search;
        window.history.replaceState(null, "", clean);
      } catch {
        // replaceState unavailable in some embedded environments — non-fatal
      }
      return hashToken;
    }
  }

  // 2. Fallback: __bu_token_<id>__ global (set by older BU bootstrap scripts)
  if (typeof window !== "undefined") {
    const globalKey = `__bu_token_${bu.id}__` as keyof Window;
    const globalToken = (window as unknown as Record<string, unknown>)[globalKey];
    if (typeof globalToken === "string" && globalToken) {
      localStorage.setItem(bu.buTokenKey, globalToken);
      return globalToken;
    }
  }

  return null;
}

/**
 * Format a relative time string ("2h", "3d", "—") for the activity
 * badge. Kept locale-neutral; unit suffix flips for Arabic.
 */
export function formatRelative(
  iso: string | null,
  lang: "en" | "ar",
): string | null {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  if (ms < 0) return null;
  const mins = Math.floor(ms / 60000);
  if (mins < 60) return lang === "ar" ? `${mins} د` : `${mins}m`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return lang === "ar" ? `${hours} س` : `${hours}h`;
  const days = Math.floor(hours / 24);
  return lang === "ar" ? `${days} ي` : `${days}d`;
}
